import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { api } from "./lib/api";
import Guard from "./components/Guard";
import AiChatWidget from "./components/AiChatWidget";
import { CookieConsent } from "./components/CookieConsent";
import { TrackingInjector } from "./components/TrackingInjector";
import { OrganizationSchema } from "./lib/seo";
import { Home, Courses, Detail, Login, Register, Marketing, AuthCallback, ForgotPassword, ResetPassword, MagicVerify } from "./pages/Home";
import { Belajar } from "./pages/Belajar";
import { Materi } from "./pages/Materi";
import { Onboarding } from "./pages/Onboarding";
import {
  JasaIklan,
  Complaint,
  About,
  FAQPage,
  Contact,
} from "./pages/Services";
import {
  Terms,
  Refund,
  Cancellation,
  Cookie,
  Disclaimer,
  AiNotice,
  IpPolicy,
} from "./pages/Legal";
import { MemberBilling } from "./pages/Billing";
import {
  Dashboard,
  Certificates,
  Orders,
  Profile,
  CertificateVerify,
  Referral,
  Mentoring,
} from "./pages/Dashboard";
import { CoursePlayer, LessonView } from "./pages/Lesson";
import { Checkout, PaymentStatus, BundleCheckout } from "./pages/Checkout";
import { BlogIndex, BlogPost } from "./pages/Blog";
import { PrivatLanding, PrivatCheckout, VipLanding } from "./pages/Privat";
import { StoreLanding, StoreDetail, StoreCheckout, MyStoreOrders } from "./pages/Store";
import {
  AdminDashboard,
  AdminCourses,
  AdminCourseEditor,
  AdminCourseProgress,
  AdminBcaManual,
  AdminDuitkuConfig,
  AdminLessonEditor,
  AdminMembers,
  AdminOrders,
  AdminCoupons,
  AdminAudit,
  AdminSettings,
  AdminBundles,
  AdminExports,
  AdminAnalytics,
  AdminMedia,
  AdminCommissions,
  AdminBlog,
  AdminMentoring,
  AdminPaymentMethods,
  AdminStore,
  AdminStoreOrders,
  AdminVip,
  AdminBranding,
  AdminIntegrations,
} from "./pages/Admin";
import AdminStoreImport from "./pages/StoreImport";
import SiteEditor from "./pages/SiteEditor";
import {
  AdminLegalPage,
  AdminInvoicesPage,
  AdminTrackingPage,
  AdminAiAnalyticsPage,
  AdminLeadsPage,
  AdminComplaintsPage,
} from "./pages/AdminPhase2";
import { AdminPages, AdminPageEditor, PublicPage } from "./pages/PageCMS";
import "@/App.css";
import { useLocation } from "react-router-dom";

function GlobalAiWidget() {
  const location = useLocation();
  const hide =
    /^\/(admin|lesson\/)/.test(location.pathname) ||
    location.pathname === "/auth/callback";
  if (hide) return null;
  return <AiChatWidget />;
}

function BrandingHead() {
  useEffect(() => {
    let interval;
    const apply = (b) => {
      const seoClaimed = document.head.getAttribute("data-seo-set") === "1";
      // Always update favicon
      if (b.favicon_url) {
        // Update all icon variants
        document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((link) => {
          link.href = b.favicon_url;
        });
        // Ensure at least one exists
        if (!document.querySelector('link[rel="icon"]')) {
          const link = document.createElement("link");
          link.rel = "icon";
          link.href = b.favicon_url;
          document.head.appendChild(link);
        }
      }
      // Update site title — but respect useSeo for page-specific titles.
      // If useSeo has claimed, only set the brand suffix once so the tab
      // still shows something. Otherwise force to the site title from DB.
      if (b.site_title) {
        if (!seoClaimed) {
          document.title = b.site_title;
        } else if (!document.title || document.title === "Emergent | Fullstack App" || document.title === "React App") {
          // Fallback if useSeo claimed but produced garbage
          document.title = b.site_title;
        }
      }
      if (!seoClaimed && b.site_description) {
        const m = document.querySelector('meta[name="description"]');
        if (m) m.setAttribute("content", b.site_description);
      }
      if (b.primary_color) {
        document.documentElement.style.setProperty("--blue", b.primary_color);
      }
    };
    api("/settings/branding").then((b) => {
      apply(b);
      // Recheck title 500ms later in case a route-level useSeo runs after us
      setTimeout(() => {
        if (document.title === "Emergent | Fullstack App" || document.title === "React App") {
          apply(b);
        }
      }, 500);
    }).catch(() => {});
    return () => interval && clearInterval(interval);
  }, []);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <BrandingHead />
      <OrganizationSchema />
      <TrackingInjector />
      <CookieConsent />
      <GlobalAiWidget />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/courses/:slug" element={<Detail />} />
        {/* Sprint 2/3: Belajar = Learning Hub (Level 0-4). Materi = flat course list. */}
        <Route path="/belajar" element={<Belajar />} />
        <Route path="/materi" element={<Materi />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/pricing" element={<Marketing page="pricing" />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/complaint" element={<Complaint />} />
        <Route path="/jasa-iklan" element={<JasaIklan />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Marketing page="privacy" />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/cancellation" element={<Cancellation />} />
        <Route path="/cookie" element={<Cookie />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/ai-notice" element={<AiNotice />} />
        <Route path="/ip-policy" element={<IpPolicy />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/auth/magic" element={<MagicVerify />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/certificate/:code" element={<CertificateVerify />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privat" element={<PrivatLanding />} />
        <Route path="/privat-1on1" element={<PrivatLanding />} />
        <Route path="/vip" element={<VipLanding />} />
        <Route path="/store" element={<StoreLanding />} />
        <Route path="/store/:slug" element={<StoreDetail />} />
        <Route
          path="/store/checkout/:slug"
          element={
            <Guard>
              <StoreCheckout />
            </Guard>
          }
        />
        <Route
          path="/dashboard/store"
          element={
            <Guard>
              <MyStoreOrders />
            </Guard>
          }
        />
        <Route
          path="/dashboard/billing"
          element={
            <Guard>
              <MemberBilling />
            </Guard>
          }
        />
        <Route
          path="/privat/checkout/:slug"
          element={
            <Guard>
              <PrivatCheckout />
            </Guard>
          }
        />

        <Route
          path="/dashboard"
          element={
            <Guard>
              <Dashboard />
            </Guard>
          }
        />
        <Route
          path="/certificates"
          element={
            <Guard>
              <Certificates />
            </Guard>
          }
        />
        <Route
          path="/orders"
          element={
            <Guard>
              <Orders />
            </Guard>
          }
        />
        <Route
          path="/profile"
          element={
            <Guard>
              <Profile />
            </Guard>
          }
        />
        <Route
          path="/referral"
          element={
            <Guard>
              <Referral />
            </Guard>
          }
        />
        <Route
          path="/mentoring"
          element={
            <Guard>
              <Mentoring />
            </Guard>
          }
        />
        <Route
          path="/checkout/:slug"
          element={
            <Guard>
              <Checkout />
            </Guard>
          }
        />
        <Route
          path="/checkout-bundle/:bundleId"
          element={
            <Guard>
              <BundleCheckout />
            </Guard>
          }
        />
        <Route
          path="/payment/:orderId"
          element={
            <Guard>
              <PaymentStatus />
            </Guard>
          }
        />
        <Route
          path="/learn/:slug"
          element={
            <Guard>
              <CoursePlayer />
            </Guard>
          }
        />
        <Route
          path="/learn/:slug/:lessonId"
          element={
            <Guard>
              <LessonView />
            </Guard>
          }
        />

        <Route
          path="/admin"
          element={
            <Guard role="ADMIN">
              <AdminDashboard />
            </Guard>
          }
        />
        <Route
          path="/admin/courses"
          element={
            <Guard role="ADMIN">
              <AdminCourses />
            </Guard>
          }
        />
        <Route
          path="/admin/courses/:id"
          element={
            <Guard role="ADMIN">
              <AdminCourseEditor />
            </Guard>
          }
        />
        <Route
          path="/admin/courses/:id/progress"
          element={
            <Guard role="ADMIN">
              <AdminCourseProgress />
            </Guard>
          }
        />
        <Route
          path="/admin/courses/:courseId/modules/:moduleId/new-lesson"
          element={
            <Guard role="ADMIN">
              <AdminLessonEditor />
            </Guard>
          }
        />
        <Route
          path="/admin/lessons/:lessonId"
          element={
            <Guard role="ADMIN">
              <AdminLessonEditor />
            </Guard>
          }
        />
        <Route
          path="/admin/members"
          element={
            <Guard role="ADMIN">
              <AdminMembers />
            </Guard>
          }
        />
        <Route
          path="/admin/orders"
          element={
            <Guard role="ADMIN">
              <AdminOrders />
            </Guard>
          }
        />
        <Route
          path="/admin/coupons"
          element={
            <Guard role="ADMIN">
              <AdminCoupons />
            </Guard>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <Guard role="ADMIN">
              <AdminAudit />
            </Guard>
          }
        />
        <Route
          path="/admin/bundles"
          element={
            <Guard role="ADMIN">
              <AdminBundles />
            </Guard>
          }
        />
        <Route
          path="/admin/exports"
          element={
            <Guard role="ADMIN">
              <AdminExports />
            </Guard>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <Guard role="ADMIN">
              <AdminAnalytics />
            </Guard>
          }
        />
        <Route
          path="/admin/media"
          element={
            <Guard role="ADMIN">
              <AdminMedia />
            </Guard>
          }
        />
        <Route
          path="/admin/commissions"
          element={
            <Guard role="ADMIN">
              <AdminCommissions />
            </Guard>
          }
        />
        <Route
          path="/admin/blog"
          element={
            <Guard role="ADMIN">
              <AdminBlog />
            </Guard>
          }
        />
        <Route
          path="/admin/mentoring"
          element={
            <Guard role="ADMIN">
              <AdminMentoring />
            </Guard>
          }
        />
        <Route
          path="/admin/payment-methods"
          element={
            <Guard role="ADMIN">
              <AdminPaymentMethods />
            </Guard>
          }
        />
        <Route
          path="/admin/store"
          element={
            <Guard role="ADMIN">
              <AdminStore />
            </Guard>
          }
        />
        <Route
          path="/editor"
          element={
            <Guard role="ADMIN">
              <SiteEditor />
            </Guard>
          }
        />
        <Route
          path="/admin/store-import"
          element={
            <Guard role="ADMIN">
              <AdminStoreImport />
            </Guard>
          }
        />
        <Route
          path="/admin/store-orders"
          element={
            <Guard role="ADMIN">
              <AdminStoreOrders />
            </Guard>
          }
        />
        <Route
          path="/admin/vip"
          element={
            <Guard role="ADMIN">
              <AdminVip />
            </Guard>
          }
        />
        <Route
          path="/admin/branding"
          element={
            <Guard role="ADMIN">
              <AdminBranding />
            </Guard>
          }
        />
        <Route
          path="/admin/pages"
          element={<Guard role="ADMIN"><AdminPages /></Guard>}
        />
        <Route
          path="/admin/pages/:pageId"
          element={<Guard role="ADMIN"><AdminPageEditor /></Guard>}
        />
        <Route
          path="/admin/duitku"
          element={<Guard role="ADMIN"><AdminDuitkuConfig /></Guard>}
        />
        <Route
          path="/admin/integrations"
          element={<Guard role="ADMIN"><AdminIntegrations /></Guard>}
        />
        <Route
          path="/admin/bca-manual"
          element={<Guard role="ADMIN"><AdminBcaManual /></Guard>}
        />
        <Route path="/p/:slug" element={<PublicPage />} />
        <Route
          path="/admin/settings"
          element={
            <Guard role="ADMIN">
              <AdminSettings />
            </Guard>
          }
        />
        <Route
          path="/admin/legal"
          element={<Guard role="ADMIN"><AdminLegalPage /></Guard>}
        />
        <Route
          path="/admin/invoices"
          element={<Guard role="ADMIN"><AdminInvoicesPage /></Guard>}
        />
        <Route
          path="/admin/tracking"
          element={<Guard role="ADMIN"><AdminTrackingPage /></Guard>}
        />
        <Route
          path="/admin/ai-analytics"
          element={<Guard role="ADMIN"><AdminAiAnalyticsPage /></Guard>}
        />
        <Route
          path="/admin/leads"
          element={<Guard role="ADMIN"><AdminLeadsPage /></Guard>}
        />
        <Route
          path="/admin/complaints"
          element={<Guard role="ADMIN"><AdminComplaintsPage /></Guard>}
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

function NotFound() {
  return (
    <div style={{
      minHeight: "80vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", padding: 40, textAlign: "center",
    }} data-testid="notfound-page">
      <h1 style={{ fontSize: "4rem", margin: 0, color: "#94a3b8" }}>404</h1>
      <h2 style={{ marginTop: 12 }}>Halaman tidak ditemukan</h2>
      <p style={{ color: "#64748b", maxWidth: 480, margin: "12px 0 24px" }}>
        Link yang Anda buka mungkin sudah dipindah atau tidak tersedia.
      </p>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
        <a href="/" className="button" data-testid="notfound-home">Kembali ke Beranda</a>
        <a href="/belajar" className="button-ghost" data-testid="notfound-belajar">Lihat Learning Hub</a>
      </div>
    </div>
  );
}
